import { isTest } from '../../src/is-env'

export function withoutActWarning(callback: () => void) {
  if (!isTest) {
    callback()
    return
  }

  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-member-access
  const act = (globalThis as any).act
  // Tests without React (CLI, node) have no act scope and nothing to warn about.
  if (typeof act !== 'function') {
    callback()
    return
  }

  // Inside a live React act queue (a scope is running, even with later scopes
  // queued behind it): mutate directly. The update joins that queue in
  // scheduling order and commits with the scope's own updates, as production
  // batches them (react-dom-client.development.js scheduleCallback$1 pushes to
  // the act queue and warns only when it is unset). Queued scopes exist only in this
  // harness; joining runs this notification ahead of them. Never wrap it in a
  // synchronous React.act instead: a render that suspends there reports
  // "A component suspended inside an act scope, but the act call was not
  // awaited" and keeps its fallback.
  // oxlint-disable-next-line typescript/no-unsafe-call, typescript/no-unsafe-member-access
  if (act.inside?.()) {
    callback()
    return
  }

  // Notifications raised before the queued scope starts share it, in call
  // order: production commits them in one render (React batches every update
  // scheduled before its render task runs), so one scope per notification would
  // commit a state production never shows (half a sign-out: resources gone,
  // session still published).
  // An explicit act() requested since the batch opened closes it: its scope queues
  // between the two, so a later notification must not join the earlier batch.
  // oxlint-disable-next-line typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access
  const requests: unknown = act.requests?.()
  if (batch && !batch.started && batch.requests === requests) {
    batch.callbacks.push(callback)
    return
  }
  const opened: Batch = { callbacks: [callback], started: false, requests }
  batch = opened
  // Production notification APIs are synchronous and cannot return a test-only
  // Promise, so nobody awaits this scope. Its failure belongs to the running
  // test: settleActNotifications() (scripts/test-setup.ts afterEach) awaits the
  // scope and rethrows, instead of an unhandled rejection landing in whatever
  // test runs when the scope settles. act.notify (react-testing act.ts) marks a
  // scope that earlier act() callers follow until it flushed; plain act otherwise.
  // oxlint-disable-next-line typescript/no-unsafe-assignment, typescript/no-unsafe-member-access
  const enqueue = act.notify ?? act
  // oxlint-disable-next-line typescript/no-unsafe-call, typescript/no-unsafe-assignment
  const scope: Promise<unknown> = Promise.resolve(enqueue(() => runBatch(opened))).catch((error: unknown) => { failures.push(error) })
  pending.add(scope)
  void scope.then(() => pending.delete(scope))
}

type Batch = { callbacks: (() => unknown)[]; started: boolean; requests: unknown }
let batch: Batch | undefined

/**
 * Every notification of the batch runs, as independent production calls would; their throws and rejections (a
 * callback may return a promise, which the scope awaits) fail the scope together.
 */
function runBatch(opened: Batch): Promise<void> | undefined {
  opened.started = true
  if (batch === opened) batch = undefined
  const errors: unknown[] = []
  const returned: unknown[] = []
  for (const callback of opened.callbacks) {
    try { returned.push(callback()) } catch (error) { errors.push(error) }
  }
  const settle = () => {
    if (errors.length === 1) throw errors[0]
    if (errors.length > 1) throw new AggregateError(errors, 'React test notifications failed inside act()')
  }
  if (!returned.some(value => value instanceof Promise)) { settle(); return undefined }
  return (async () => {
    for (const result of await Promise.allSettled(returned)) if (result.status === 'rejected') errors.push(result.reason)
    settle()
  })()
}

/**
 * Like withoutActWarning, but the callback has been applied when this returns, in tests too. For
 * production APIs that hand a value back synchronously (Base UI's toastManager.promise() returns
 * the promise its provider sets during delivery). A throw reaches the caller, as in production.
 * Deliver a whole event stream through it: it does not order behind withoutActWarning's queue.
 */
export function withoutActWarningSync(callback: () => void) {
  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-member-access
  const sync = isTest ? (globalThis as any).act?.sync : undefined
  if (typeof sync !== 'function') {
    callback()
    return
  }
  // oxlint-disable-next-line typescript/no-unsafe-call
  sync(callback)
}

const pending = new Set<Promise<unknown>>()
const failures: unknown[] = []

/**
 * Awaits notifications withoutActWarning queued in act, then consumes their failures and the test's act warnings
 * together so both fail the test that caused them (called after each test).
 */
export async function settleActNotifications(actWarnings: string[] = []): Promise<void> {
  while (pending.size > 0) await Promise.all(pending)
  const failed = failures.splice(0)
  const warnings = actWarnings.splice(0)
  if (warnings.length > 0) failed.push(new Error(`React state updated outside act():\n${warnings.join('\n')}`))
  if (failed.length === 1) throw failed[0]
  if (failed.length > 1) throw new AggregateError(failed, 'React test notifications failed inside act()')
}
